/**
 * Helpers for detecting and forwarding Storyblok Visual Editor sessions.
 *
 * When a page is opened inside the Storyblok Visual Editor iframe, Storyblok appends a
 * signed token to the URL via the `_storyblok_tk[*]` query parameters:
 *   - `_storyblok_tk[space_id]`
 *   - `_storyblok_tk[timestamp]`
 *   - `_storyblok_tk[token]`
 *
 * The presence of these parameters tells the app it is being previewed in the editor, so it
 * should request draft content and forward the token to the API for server-side validation.
 */

const TK_PREFIX = "_storyblok_tk";

const TK_KEYS = [
  `${TK_PREFIX}[space_id]`,
  `${TK_PREFIX}[timestamp]`,
  `${TK_PREFIX}[token]`,
] as const;

/**
 * Whether the current request is a Storyblok Visual Editor preview session.
 */
export const isStoryblokEditor = (searchParams: URLSearchParams): boolean =>
  TK_KEYS.every((key) => searchParams.has(key));

/**
 * Extract the `_storyblok_tk[*]` parameters so they can be forwarded to the API for
 * server-side signature validation.
 */
export const getEditorTokenParams = (
  searchParams: URLSearchParams
): Record<string, string> => {
  const params: Record<string, string> = {};
  for (const key of TK_KEYS) {
    const value = searchParams.get(key);
    if (value !== null) {
      params[key] = value;
    }
  }
  return params;
};
